import * as React from 'react';

// The settings section that downloads everything the app has stored, drawn
// from props only: a button, and a status line while the files are read and
// once the download has been handed to the browser.

export type ExportStatus =
  | { kind: 'idle' }
  | { kind: 'reading'; files: number }
  | { kind: 'done'; files: number; format: 'zip' | 'json'; bytes: number }
  | { kind: 'error'; message: string };

export interface ExportSectionProps {
  status: ExportStatus;
  onDownload: () => void;
}

function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function statusText(status: ExportStatus): string {
  switch (status.kind) {
    case 'idle':
      return '';
    case 'reading':
      return `Reading ${status.files} file${status.files === 1 ? '' : 's'}…`;
    case 'done':
      return `Downloaded ${status.files} file${status.files === 1 ? '' : 's'} as ${status.format === 'zip' ? 'a ZIP archive' : 'JSON'} (${size(status.bytes)}).`;
    case 'error':
      return `Could not export: ${status.message}`;
  }
}

export const ExportSection: React.FC<ExportSectionProps> = ({ status, onDownload }) => (
  <section className="panel settings-export">
    <h2>Your data</h2>
    <p className="settings-export-intro">
      Everything the app keeps in this browser — your games, their scans and your settings — as one file you can keep or move elsewhere.
    </p>
    <div className="settings-export-actions">
      <button type="button" className="btn btn-secondary" onClick={onDownload} disabled={status.kind === 'reading'} aria-busy={status.kind === 'reading'}>
        Download all data
      </button>
      <span className="settings-export-status" role="status" data-status={status.kind}>
        {statusText(status)}
      </span>
    </div>
  </section>
);
